export {debounce} from 'debounce';

import Component from '../component/component';

import './helpers.scss';

export type DebouncedFunction<F> = F & { clear(): void } & { flush(): void };

export interface IGaEventParams {
    category: string;
    action: string;
    label?: string;
    value?: number;
}

export default class Helpers {

    static defaults: IGotoAndPlay = {
        ajax: {
            isEnabled: false,
            keptBodyClasses: [],
            templateName: '',
        },
        ajaxPath: '',
        assetsPath: '',
        components: {},
        cookieDomain: '',
        cookiePath: '',
        googleMapsApiKey: null,
        headerOffset: 0,
        initializedComponents: [],
        lightboxApiKey: 'W5ZTE-CNJCG-6ARFR-HNSBU',
        lightboxLoaded: false,
        loggedIn: false,
        nonce: '',
        notifications: [],
        sitePath: '',
        svgPath: '',
        templatePath: '',
        version: '',
        vimeoApiLoaded: false,
    };

    /* eslint-disable */ // this lets the breakpoints stay in numerical order, not alphabetical
    static bp: {[key: string]: number} = {
        xs: 360,
        sm: 479,
        md: 768,
        lg: 1089,
        xl: 1301,
    };
    /* eslint-enable */

    static visible(element: HTMLElement): boolean {
        return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    }

    static hasClass(target: HTMLElement, classNames: string): boolean {
        const classList: string[] = classNames.split('.').filter((name: string) => !!name);

        return classList
            .filter((className: string): boolean => target.classList.contains(className))
            .length > 0;
    }

    static scrollToTarget(target: HTMLElement, offsetTop: number = 0): void {
        const header: HTMLElement = document.querySelector('.header');
        let headerHeight: number = 0;

        if (header !== undefined && header !== null) {
            headerHeight = header.offsetHeight;
        }

        const scrollTop: number = target.offsetTop - offsetTop - headerHeight;

        scroll({
            behavior: 'smooth',
            top: scrollTop,
        });
    }

    static parents(target: HTMLElement, querySelector?: string): HTMLElement[] {
        const parents: HTMLElement[] = [];
        let current: Node = target;

        if (current !== null) {
            while (current.parentNode !== null && current.parentNode !== document.documentElement) {
                if (current.parentNode instanceof HTMLElement && (!querySelector || Helpers.hasClass(current.parentNode, querySelector))) {
                    parents.push(current.parentNode);
                }

                current = current.parentNode;
            }
        }

        return parents;
    }

    static isOnScreen(target: HTMLElement, partial: boolean = true): boolean {
        const viewTop: number = window.scrollY;
        const viewBottom: number = viewTop + window.innerHeight;
        const top: number = target.offsetTop;
        const bottom: number = top + target.offsetHeight;
        const compareTop: number = partial === true ? bottom : top;
        const compareBottom: number = partial === true ? top : bottom;

        return ((compareBottom <= viewBottom) && (compareTop >= viewTop));
    }

    static disableScroll(): void {
        if (!Helpers.isScrollDisabled()) {
            const curScroll: number = window.scrollY;
            const page: HTMLElement = document.getElementById('page');

            document.body.classList.add('is-scroll-disabled');
            if (page) {
                page.style.top = -curScroll + 'px';
            }

            window.scrollTo(0, 0);
        }
    }

    static enableScroll(): void {
        const page: HTMLElement = document.getElementById('page');
        const bodyScroll: number = page ? parseInt((page.style.top || '0'), 10) : 0;

        document.body.classList.remove('is-scroll-disabled');
        if (bodyScroll) {
            window.scrollTo(0, -bodyScroll);

            if (page) {
                page.style.top = '0px';
            }
        }
    }

    static isScrollDisabled(): boolean {
        return document.body.classList.contains('is-scroll-disabled');
    }

    static isURL(str: string): boolean {
        const regexp: RegExp = /^https?:\/\//i;

        return regexp.test(str);
    }

    static toFixed(n: number, prec: number): number {
        // Fix for IE parseFloat(0.55).toFixed(0) = 0;
        const k: number = Math.pow(10, prec);

        return Math.round(n * k) / k;
    }

    static formatNumber(num: number, decimals: number = 2, decPoint: string = '.', thousandsSep: string = ''): string {
        const n: number = !isFinite(+num) ? 0 : +num;
        const prec: number = !isFinite(+decimals) ? 0 : Math.abs(decimals);
        const sep: string = thousandsSep === null ? ' ' : thousandsSep;
        const dec: string = decPoint === null ? ',' : decPoint;
        const s: string[] = (prec ? Helpers.toFixed(n, prec) : Math.round(n)).toString().split('.');

        if (s[0].length > 3) {
            s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
        }
        if ((s[1] || '').length < prec) {
            s[1] = s[1] || '';
            s[1] += new Array(prec - s[1].length + 1).join('0');
        }

        return s.join(dec);
    }

    static parseFloat(val: string): number {
        const num: number = parseFloat(val);

        if (typeof num === 'number' && !isNaN(num)) {
            return num;
        } else {
            return 0;
        }
    }

    static get isIE(): boolean | number {

        const ua: string = window.navigator.userAgent;
        const msie: number = ua.indexOf('MSIE ');

        if (msie > 0) {
            // IE 10 or older => return version number

            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }

        const trident: number = ua.indexOf('Trident/');

        if (trident > 0) {
            // IE 11 => return version number
            const rv: number = ua.indexOf('rv:');

            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        const edge: number = ua.indexOf('Edge/');

        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }

        // other browser

        return false;
    }

    static get isMobileDevice(): boolean {

        return ((/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent)
            || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(navigator.userAgent.substr(0, 4))) ? true : false);
    }

    static trackEvent(params: IGaEventParams): void {
        if (typeof ga !== 'undefined') {
            ga('send', {
                eventAction: params.action,
                eventCategory: params.category,
                eventLabel: params.label,
                eventValue: params.value,
                hitType: 'event',
            });
        }

        if (typeof gtag !== 'undefined') {
            gtag('event', params.action, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                event_category: params.category,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                event_label: params.label,
                value: params.value,
            });
        }
    }

    static trackPageChange(path: string): void {
        if (typeof ga !== 'undefined') {
            ga('set', 'page', path);
            ga('send', 'pageview');
        }

        if (typeof gtag !== 'undefined') {
            gtag('event', 'screen_view', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                screen_name: path,
            });
        }
    }

    static convertToInt(value: string | number | undefined): number {
        const numeric: number = parseInt('' + value, 10);

        return isNaN(numeric) ? 0 : numeric;
    }

    static outsideClick(event: MouseEvent, htmlElement: HTMLElement): boolean {
        if (event.target === htmlElement || htmlElement.contains(event.target as Element)) {
            return false;
        }

        return true;
    }

    static init(): void {
        // setup defaults variables
        Object.keys(Helpers.defaults).forEach((key: string): void => {
            if (window.gotoAndPlay[key] === undefined) {
                window.gotoAndPlay[key] = Helpers.defaults[key];
            }
        });
        // run after document ready
        document.addEventListener('DOMContentLoaded', (): void => {
            // init components
            if (window.gotoAndPlay && Object.prototype.hasOwnProperty.call(window.gotoAndPlay, 'components')) {
                Object.keys(window.gotoAndPlay.components).forEach((name: string): void => {
                    const component: unknown = window.gotoAndPlay.components[name];

                    if (Component.is(component)) {
                        component.enhance();
                    }
                });
                document.dispatchEvent(new CustomEvent('enhance'));
            }
        });

        document.addEventListener('click', (event: Event) => {
            const target: HTMLElement = event.target as HTMLElement;

            if (target.classList.contains('js-scroll-to')) {
                const currentPage: Location = window.location || window.document.location;
                const currentPath: string = currentPage.href.split('#')[0];
                const targetPath: string = target.getAttribute('href').split('#')[0];
                const targetSelector: string = target.getAttribute('href').split('#')[1];

                if (currentPath === targetPath || targetPath === '') {
                    event.preventDefault();
                    const targetSection: HTMLElement = document.querySelector('#' + targetSelector);

                    Helpers.scrollToTarget(targetSection);
                }
            }
        });

        // Calc app height
        document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty('--app-width', `${document.body.clientWidth}px`);

        window.addEventListener('resize', () => {
            document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
            document.documentElement.style.setProperty('--app-width', `${document.body.clientWidth}px`);
        });

    }
}

Helpers.init();
